// „Konflikte vorhersagen" (HazardMatrixView): die Antwort der KI lesen.
//
// Der gemini-proxy liefert für `predictMutualHazards` laut seinem Prompt
// `{ hazards: [{ gewerkA, gewerkB, gefaehrdung, massnahme, prioritaet }] }`
// (supabase/functions/gemini-proxy/index.ts, Fall "predictMutualHazards") —
// ohne Schema, nur mit responseMimeType JSON. Die Matrix erwartete seit dem
// Umzug auf den Proxy (e60389f) weiter das alte Format: ein Array aus
// tradeSource/tradeAffected/description/measure/severity. Das Objekt landete
// ungeprüft im State, und `.map` darauf riss die Ansicht mit.
//
// Deshalb wird die Antwort hier gelesen statt durchgereicht: beide Formen,
// Namen auf die Achsen der Matrix, Stufe auf low/medium/high. Was sich nicht
// lesen lässt, wird zur Meldung — nie zum Absturz und nie zu „keine Konflikte".
// Rein und ohne DOM, damit es ohne jsdom prüfbar ist.

import type { MutualHazard } from '../types';
import { SEVERITY_RANG, vergleichsform } from './koordinierung/uebernahmeMatrix';

/** Ein Vorschlag der KI: eine Zelle der Matrix, noch ohne id. */
export type KonfliktVorschlag = Omit<MutualHazard, 'id'>;

export interface KonfliktVorhersage {
    /** Übernehmbar: beide Namen sind Achsen der Matrix, je Zelle ein Eintrag. */
    vorschlaege: KonfliktVorschlag[];
    /** Satz für den KI-Kasten, wenn es nichts anzuzeigen gibt — sonst null. */
    meldung: string | null;
    /** Zusatz über der Vorschlagsliste (unvollständig, ausgelassen) — sonst null. */
    hinweis: string | null;
}

type Eintrag = Record<string, unknown>;

const istEintrag = (wert: unknown): wert is Eintrag =>
    wert !== null && typeof wert === 'object' && !Array.isArray(wert);

/** Erster nichtleerer Text unter einem der Namen. */
const text = (eintrag: Eintrag, ...namen: string[]): string => {
    for (const name of namen) {
        const wert = eintrag[name];
        if (typeof wert === 'string' && wert.trim()) return wert.trim();
    }
    return '';
};

/**
 * Wo die Liste steht: ein Array direkt (altes Format), `hazards` laut Prompt,
 * `items`, wenn der Proxy ein abgeschnittenes Array einpackt — sonst das erste
 * Array der Antwort. `{ text }` (Proxy konnte nicht parsen) hat keines.
 */
const listeAus = (antwort: unknown): unknown[] | null => {
    if (Array.isArray(antwort)) return antwort;
    if (!istEintrag(antwort)) return null;
    if (Array.isArray(antwort.hazards)) return antwort.hazards;
    if (Array.isArray(antwort.items)) return antwort.items;
    return Object.values(antwort).find((wert): wert is unknown[] => Array.isArray(wert)) ?? null;
};

const STUFEN: Record<string, MutualHazard['severity']> = {
    hoch: 'high', high: 'high',
    mittel: 'medium', medium: 'medium',
    niedrig: 'low', gering: 'low', low: 'low',
};

/** Priorität der KI (auch englisch) → Stufe; Unbekanntes wie in der Übernahme: mittel. */
const stufe = (wert: string): MutualHazard['severity'] => {
    const schluessel = wert.toLowerCase();
    return Object.hasOwn(STUFEN, schluessel) ? STUFEN[schluessel] : 'medium';
};

/**
 * Der Name der KI als Achse der Matrix. Der Prompt verlangt die Namen
 * wortgetreu; weicht die KI ab („Elektro Meier" statt „Elektro Meier GmbH"),
 * zählt nur ein EINDEUTIGER Treffer in der Vergleichsform der Übernahme.
 * Ein fremder Name legte eine Zelle an, die die Matrix nie zeigt.
 */
const achseFuer = (name: string, achsen: readonly string[]): string | null => {
    if (!name) return null;
    if (achsen.includes(name)) return name;
    const form = vergleichsform(name);
    if (!form) return null;
    const treffer = achsen.filter((achse) => vergleichsform(achse) === form);
    return treffer.length === 1 ? treffer[0] : null;
};

/**
 * Hängt die Zeilen von `neu` unter `bisher` an. `bisher` bleibt, wie es ist;
 * ausgelassen wird nur, was dort schon als Zeile steht — sonst stünde derselbe
 * Satz nach einem zweiten Lauf doppelt in der Zelle.
 */
const anhaengen = (bisher: string, neu: string): string => {
    const vorhanden = new Set(bisher.split('\n').map((zeile) => zeile.trim()));
    const dazu: string[] = [];
    for (const zeile of neu.split('\n').map((z) => z.trim())) {
        if (!zeile || vorhanden.has(zeile)) continue;
        vorhanden.add(zeile);
        dazu.push(zeile);
    }
    if (dazu.length === 0) return bisher;
    return bisher ? `${bisher}\n${dazu.join('\n')}` : dazu.join('\n');
};

/** Die schwerere von zwei Stufen; Unbekanntes zählt als nichts. */
const schwerer = (
    a: MutualHazard['severity'],
    b: MutualHazard['severity'],
): MutualHazard['severity'] => ((SEVERITY_RANG[b] ?? 0) > (SEVERITY_RANG[a] ?? 0) ? b : a);

/** Steht in dem Eintrag überhaupt etwas? Ein leerer fällt still weg. */
const hatInhalt = (eintrag: Eintrag): boolean =>
    Object.values(eintrag).some((wert) =>
        (typeof wert === 'string' ? wert.trim() !== '' : wert !== null && wert !== undefined));

const NICHT_LESBAR = 'Die Antwort der KI war nicht lesbar – bitte erneut versuchen.';

/**
 * Antwort der KI → übernehmbare Vorschläge.
 *
 * Mehrere Gefährdungen für DIESELBE Zelle werden zusammengefasst (Texte
 * untereinander, die schwerste Stufe gewinnt): „Übernehmen" nimmt die Zelle als
 * Ganzes und räumt alle Vorschläge dieses Paares ab — getrennt ginge der zweite
 * verloren. Leere Einträge tragen nichts und fallen still weg. Einträge MIT
 * Text, die keinem Gewerkepaar zuzuordnen sind, und Einträge mit Inhalt, aber
 * ohne lesbare Gefährdung oder Maßnahme (andere Feldnamen), werden gezählt und
 * genannt — sonst hieße eine unlesbare Antwort „keine Vorschläge".
 */
export const leseKonfliktVorhersage = (
    antwort: unknown,
    achsen: readonly string[],
): KonfliktVorhersage => {
    const liste = listeAus(antwort);
    if (!liste) {
        return { vorschlaege: [], meldung: NICHT_LESBAR, hinweis: null };
    }
    const abgeschnitten = istEintrag(antwort) && antwort._truncated === true;

    const zellen = new Map<string, KonfliktVorschlag>();
    let ausgelassen = 0;
    let unlesbar = 0;
    for (const roh of liste) {
        if (!istEintrag(roh)) {
            ausgelassen += 1;
            continue;
        }
        const description = text(roh, 'gefaehrdung', 'description', 'gefährdung');
        const measure = text(roh, 'massnahme', 'measure', 'maßnahme');
        if (!description && !measure) {
            if (hatInhalt(roh)) unlesbar += 1;
            continue;
        }

        const source = achseFuer(text(roh, 'gewerkA', 'tradeSource', 'gewerk_a'), achsen);
        const affected = achseFuer(text(roh, 'gewerkB', 'tradeAffected', 'gewerk_b'), achsen);
        // Die Matrix sperrt die Diagonale: ein Gewerk gefährdet sich nicht selbst.
        if (!source || !affected || source === affected) {
            ausgelassen += 1;
            continue;
        }

        const severity = stufe(text(roh, 'prioritaet', 'severity', 'priorität'));
        const schluessel = `${source}\u0000${affected}`;
        const zelle = zellen.get(schluessel);
        if (!zelle) {
            zellen.set(schluessel, { tradeSource: source, tradeAffected: affected, description, measure, severity });
            continue;
        }
        zelle.description = anhaengen(zelle.description, description);
        zelle.measure = anhaengen(zelle.measure, measure);
        zelle.severity = schwerer(zelle.severity, severity);
    }

    const vorschlaege = [...zellen.values()];
    if (vorschlaege.length === 0) {
        let meldung: string;
        if (unlesbar > 0) meldung = NICHT_LESBAR;
        else if (ausgelassen > 0) meldung = 'Kein Vorschlag der KI passte zu den Gewerken der Matrix – bitte erneut versuchen.';
        else if (abgeschnitten) meldung = 'Die Antwort der KI war unvollständig – bitte erneut versuchen.';
        // Nur leere Einträge: eine Liste kam, aber ohne jede Aussage.
        else if (liste.length > 0) meldung = NICHT_LESBAR;
        else meldung = 'Die KI hat keine Vorschläge geliefert.';
        return { vorschlaege, meldung, hinweis: null };
    }

    const teile: string[] = [];
    if (abgeschnitten) teile.push('Die Antwort der KI war unvollständig – es können Vorschläge fehlen.');
    if (ausgelassen > 0) {
        teile.push(`${ausgelassen} ${ausgelassen === 1 ? 'Vorschlag ließ' : 'Vorschläge ließen'} sich keinem Gewerk zuordnen.`);
    }
    if (unlesbar > 0) {
        teile.push(`${unlesbar} ${unlesbar === 1 ? 'Vorschlag war' : 'Vorschläge waren'} nicht lesbar.`);
    }
    return { vorschlaege, meldung: null, hinweis: teile.length > 0 ? teile.join(' ') : null };
};

/**
 * „Übernehmen": einen Vorschlag in die Matrix schreiben.
 *
 * Die KI kennt die belegten Zellen nicht. Eine zweite Zeile für dieselbe Zelle
 * zeigte die Matrix aber nie (sie liest die erste), und das Speichern im
 * Zellen-Editor löschte sie wortlos. Deshalb wie „Zusammenführen" in der
 * Übernahme aus der Koordinierung (uebernehmeInMatrix): der Bestand bleibt
 * vorn und behält id und Platz, der Vorschlag kommt darunter, die schwerere
 * Stufe gewinnt. Stehen schon mehrere Zeilen in der Zelle, gehen sie in die
 * erste ein. Eine freie Zelle wird wie bisher hinten angelegt.
 */
export const vorschlagUebernehmen = (
    hazards: readonly MutualHazard[],
    vorschlag: KonfliktVorschlag,
    neueId: () => string,
): MutualHazard[] => {
    const inZelle = (h: MutualHazard): boolean =>
        h.tradeSource === vorschlag.tradeSource && h.tradeAffected === vorschlag.tradeAffected;
    const bestand = hazards.filter(inZelle);
    if (bestand.length === 0) return [...hazards, { id: neueId(), ...vorschlag }];

    const bestandText = (feld: 'description' | 'measure'): string =>
        bestand
            .map((h) => (typeof h[feld] === 'string' ? h[feld].trim() : ''))
            .filter(Boolean)
            .join('\n');
    const erste = bestand[0];
    const zelle: MutualHazard = {
        ...erste,
        description: anhaengen(bestandText('description'), vorschlag.description),
        measure: anhaengen(bestandText('measure'), vorschlag.measure),
        severity: bestand.reduce((stufe, h) => schwerer(stufe, h.severity), vorschlag.severity),
    };
    const ergebnis: MutualHazard[] = [];
    for (const h of hazards) {
        if (h === erste) ergebnis.push(zelle);
        else if (!inZelle(h)) ergebnis.push(h);
    }
    return ergebnis;
};

/** Ruhige Zeile im KI-Kasten nach einem Lauf: wo die Vorschläge stehen. */
export const vorschlaegeZeile = (anzahl: number): string =>
    `${anzahl} ${anzahl === 1 ? 'Vorschlag' : 'Vorschläge'} – unten unter „Vorschläge“`;

/** Meldung, wenn schon der Aufruf scheitert (Netz, Proxy, Dienst). */
export const vorhersageFehlerText = (online: boolean | undefined): string =>
    online === false
        ? 'KI braucht Netz – bitte später erneut versuchen.'
        : 'KI nicht erreichbar – bitte später erneut versuchen.';
